import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGauge,
    faFileInvoiceDollar,
    faFileAlt,
    faUsers,
    faListCheck,
    faBars,
    faTimes,
    faHeadset,
    faDiagramProject,
    faMoon,
    faSun,
    faEnvelope,
    faUserGear,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import api from "../../api/client";
import "./DashboardLayout.css";

const AdminNav = () => {
    const { t } = useTranslation();
    const [pendingCount, setPendingCount] = useState(0);
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        api.clientCounts()
            .then((res) => setPendingCount(Number(res.counts?.pending || 0)))
            .catch(() => {});
        api.unreadMessages()
            .then((res) =>
                setUnread(
                    Number(res.count || 0) + Number(res.contact_count || 0),
                ),
            )
            .catch(() => {});
    }, []);

    return (
        <>
            <NavLink
                to="/admin"
                end
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faGauge} />
                <span>{t("dashboard.welcome")}</span>
            </NavLink>
            <NavLink
                to="/admin/clients"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faUsers} />
                <span>
                    {t("clients.title")}
                    {pendingCount > 0 && (
                        <span className="dash-nav__badge">{pendingCount}</span>
                    )}
                </span>
            </NavLink>
            <NavLink
                to="/admin/billing"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                <span>Billing</span>
            </NavLink>
            <NavLink
                to="/admin/projects"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faDiagramProject} />
                <span>Projects</span>
            </NavLink>
            <NavLink
                to="/admin/messages"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>
                    Messages
                    {unread > 0 && (
                        <span className="dash-nav__badge">{unread}</span>
                    )}
                </span>
            </NavLink>
            <NavLink
                to="/admin/services"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faListCheck} />
                <span>{t("services.title")}</span>
            </NavLink>
            <NavLink
                to="/admin/account"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faUserGear} />
                <span>My Account</span>
            </NavLink>
        </>
    );
};

const ClientNav = () => {
    const { t } = useTranslation();
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        api.unreadMessages()
            .then((res) => setUnread(Number(res.count || 0)))
            .catch(() => {});
    }, []);

    return (
        <>
            <NavLink
                to="/dashboard"
                end
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faGauge} />
                <span>{t("nav.dashboard")}</span>
            </NavLink>
            <NavLink
                to="/dashboard/quotes"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faFileAlt} />
                <span>{t("quote.title")}</span>
            </NavLink>
            <NavLink
                to="/dashboard/invoices"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                <span>{t("invoice.title")}</span>
            </NavLink>
            <NavLink
                to="/dashboard/projects"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faDiagramProject} />
                <span>Projects</span>
            </NavLink>
            <NavLink
                to="/dashboard/messages"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>
                    Messages
                    {unread > 0 && (
                        <span className="dash-nav__badge">{unread}</span>
                    )}
                </span>
            </NavLink>
            <NavLink
                to="/dashboard/account"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faUserGear} />
                <span>My Account</span>
            </NavLink>
            <NavLink
                to="/dashboard/support"
                className={({ isActive }) =>
                    `dash-nav__link ${isActive ? "dash-nav__link--active" : ""}`
                }
            >
                <FontAwesomeIcon icon={faHeadset} />
                <span>Contact Support</span>
            </NavLink>
        </>
    );
};

const DashboardLayout = ({ children }) => {
    const { user, logout, isImpersonating, stopImpersonating } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/client-portal");
    };

    return (
        <div className="dashboard">
            {isImpersonating && (
                <div className="dashboard__impersonate-bar">
                    <span>
                        Viewing the portal as <strong>{user?.name}</strong> —
                        this is exactly what they see.
                    </span>
                    <button onClick={stopImpersonating}>Return to Admin</button>
                </div>
            )}
            <button
                className="dashboard__mobile-toggle"
                onClick={() => setSidebarOpen((o) => !o)}
            >
                <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />
            </button>

            <aside
                className={`dashboard__sidebar ${sidebarOpen ? "dashboard__sidebar--open" : ""}`}
            >
                <div className="dash-nav">
                    <div className="dash-nav__user">
                        <div className="dash-nav__avatar">
                            {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="dash-nav__user-info">
                            <span className="dash-nav__user-name">
                                {user?.name}
                            </span>
                            <span className="dash-nav__user-role">
                                {user?.role}
                            </span>
                        </div>
                    </div>
                    <nav className="dash-nav__links">
                        {user?.role === "admin" ? <AdminNav /> : <ClientNav />}
                    </nav>
                    <button
                        className="dash-nav__theme"
                        onClick={toggleTheme}
                        title={
                            theme === "dark"
                                ? "Switch to light mode"
                                : "Switch to dark mode"
                        }
                    >
                        <FontAwesomeIcon
                            icon={theme === "dark" ? faSun : faMoon}
                        />
                        <span>
                            {theme === "dark" ? "Light Mode" : "Dark Mode"}
                        </span>
                    </button>
                    <button className="dash-nav__logout" onClick={handleLogout}>
                        {t("nav.logout")}
                    </button>
                </div>
            </aside>

            <main
                className="dashboard__main"
                onClick={() => setSidebarOpen(false)}
            >
                {children}
            </main>
        </div>
    );
};

export default DashboardLayout;
